import type {
  BucketWeightLike,
  DimensionExplanationLike,
  ModelExplanationLike,
} from '@/lib/lookalike-model';
import {
  caveatText,
  curvePointAt,
  curveStops,
  factorContributions,
  fitPendingCopy,
  fitPendingHeading,
  isSuppressed,
  shrinkagePercent,
  suppressionReasonOf,
  suppressionText,
} from '@/lib/lookalike-model';
import { fmtDateTime } from '@/lib/datetime';

/// How the model assembled a lookalike audience, rendered as factors and weights.
///
/// A deliberate **sibling** to `InsightBreakdown` rather than a reuse of it. That component's
/// numeric slot is a user count and its bar is `users / total`; these numbers are signed
/// log-lift divergences whose sum is not meaningful, so borrowing the headcount bar would
/// render a ratio as a population.
///
/// Every dimension the model fitted appears, including the withheld ones — and a withheld row
/// renders as an *absent* dashed track, never an empty bar. That distinction is the reason the
/// wire type carries `suppressed_dimensions` separately from `dimensions`: a suppressed factor
/// still has a fitted weight and still moved the score, so showing it as zero would tell an
/// operator it did not matter.

const DIMENSION_LABELS: Record<string, string> = {
  age_range: 'Age',
  gender: 'Gender',
  income: 'Household income',
  education: 'Education',
  household_has_children: 'Children at home',
  distance_band: 'Distance from a venue',
  credit_score: 'Credit score',
  religion: 'Religion',
};

function dimensionLabel(dimension: string): string {
  return DIMENSION_LABELS[dimension] ?? dimension.replace(/_/g, ' ');
}

function fmtCount(value: bigint | number): string {
  return Number(value).toLocaleString();
}

function fmtSignedWeight(weight: bigint | number): string {
  const numeric = Number(weight);
  // The sign is printed as well as coloured. Colour alone fails for the ~8% of men with a
  // red/green deficiency, and this is the number an operator acts on.
  return `${numeric > 0 ? '+' : ''}${numeric}`;
}

/// The strongest pull in a dimension, as prose an operator can act on.
function strongestPhrase(bucket: BucketWeightLike | null): string {
  if (!bucket) return '';
  const direction = Number(bucket.weight) >= 0 ? 'more' : 'less';
  return `${bucket.display_label} — ${direction} likely`;
}

export function LookalikeFactorBreakdown({ model }: { model: ModelExplanationLike }) {
  if (model.status !== 'ready') {
    return (
      <div className="audience-planner-analysis-empty">
        <div>
          <strong>No model published yet.</strong>{' '}
          {model.fit_requested_at
            ? 'A fit is running now — the factors and size usually appear within a few minutes.'
            : 'A lookalike is fitted overnight. If the seed audience was just created or edited, its factors appear after the next run.'}
        </div>
      </div>
    );
  }

  const factors = factorContributions(model);
  // Withheld dimensions are appended rather than interleaved: they carry no magnitude to rank
  // by, and inventing a position for them would imply one.
  const withheld = model.suppressed_dimensions;

  return (
    <div className="lam-factors">
      {factors.map((factor) => (
        <div className="lam-factor" key={factor.dimension}>
          <span className="lam-factor-label">{dimensionLabel(factor.dimension)}</span>
          <span className="lam-factor-track">
            <span
              className="lam-factor-fill"
              style={{ width: `${Math.round(factor.fraction * 100)}%` }}
            />
          </span>
          <span className="lam-factor-value">{strongestPhrase(factor.strongest)}</span>
        </div>
      ))}
      {withheld.map((entry) => (
        <div className="lam-factor" key={entry.dimension}>
          <span className="lam-factor-label">{dimensionLabel(entry.dimension)}</span>
          <span
            className="lam-factor-track is-suppressed"
            title={suppressionText(entry.reason)}
            aria-label={suppressionText(entry.reason)}
          >
            <span className="lam-factor-fill" />
          </span>
          <span className="lam-factor-value is-suppressed">
            {entry.reason === 'below_privacy_floor' ? 'withheld' : 'not measured'}
          </span>
        </div>
      ))}
    </div>
  );
}

/// Every bucket of one dimension, with its signed weight and the two shares it came from.
export function LookalikeDimensionWeights({
  dimension,
  model,
}: {
  dimension: DimensionExplanationLike;
  model: ModelExplanationLike;
}) {
  if (isSuppressed(model, dimension.dimension)) {
    const reason = suppressionReasonOf(model, dimension.dimension);
    return (
      <div className="lam-caveats">
        <div className="lam-caveat">
          <span className="lam-caveat-mark">—</span>
          <span>{reason ? suppressionText(reason) : 'Withheld.'}</span>
        </div>
      </div>
    );
  }

  // Scaled against the largest absolute weight in THIS dimension, so a dimension whose weights
  // are all small is still readable. A shared scale across dimensions would flatten five of
  // them behind whichever one happens to be strongest.
  const widest = dimension.buckets.reduce(
    (largest, bucket) => Math.max(largest, Math.abs(Number(bucket.weight))),
    0,
  );

  return (
    <div className="lam-weights">
      {dimension.buckets.map((bucket) => {
        const weight = Number(bucket.weight);
        const half = widest > 0 ? (Math.abs(weight) / widest) * 50 : 0;
        return (
          <div className="lam-weight" key={`${bucket.order}-${bucket.display_label}`}>
            <span>{bucket.display_label}</span>
            <span className="lam-weight-bar">
              <span
                className={`lam-weight-fill ${weight >= 0 ? 'is-positive' : 'is-negative'}`}
                style={{ width: `${half}%` }}
              />
            </span>
            <span className="lam-weight-number">{fmtSignedWeight(weight)}</span>
            <span />
            <span className="lam-weight-shares">
              {bucket.seed_percentage.toFixed(1)}% of the seed vs{' '}
              {bucket.baseline_percentage.toFixed(1)}% of all buyers
            </span>
            <span />
          </div>
        );
      })}
    </div>
  );
}

/// The chosen size, the stops it can move between, and every caveat attached to the number.
///
/// `onSelectStop` is optional: the Planner renders this read-only, while the builder passes a
/// handler. The stops come from the published curve, so changing one is a local read — no
/// request is issued, which is why they are buttons rather than a slider bound to a query.
export function LookalikeSelection({
  model,
  shareBps,
  onSelectStop,
}: {
  model: ModelExplanationLike;
  shareBps: number;
  onSelectStop?: (shareBps: number) => void;
}) {
  if (model.status !== 'ready') {
    const insufficient = model.status === 'insufficient_data';
    return (
      <div className="audience-planner-analysis-empty lam-model-pending" role="status">
        <span className="audience-planner-empty-icon" aria-hidden="true">…</span>
        <div>
          <strong>
            {insufficient ? 'Seed data is not sufficient yet' : fitPendingHeading(model.fit_requested_at)}
          </strong>
          <span>
            {insufficient
              ? 'A size cannot be published until the seed has enough matched people. This is not a measured zero-person audience.'
              : fitPendingCopy(model.fit_requested_at)}
          </span>
        </div>
      </div>
    );
  }

  const point = curvePointAt(model, shareBps);
  const stops = curveStops(model);

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div className="lam-selection">
        <span className="lam-selection-count">{point ? fmtCount(point.people) : '—'}</span>
        <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
          people at the top {point?.share_bps ?? shareBps} bps
        </span>
        <span className="lam-modelled-badge">Modelled</span>
      </div>

      {stops.length > 0 && (
        <div className="lam-stops">
          {stops.map((stop) => (
            <button
              type="button"
              key={stop}
              className="lam-stop"
              aria-pressed={stop === (point?.share_bps ?? shareBps)}
              disabled={!onSelectStop}
              onClick={onSelectStop ? () => onSelectStop(stop) : undefined}
            >
              {stop} bps
            </button>
          ))}
        </div>
      )}

      <div className="lam-caveats">
        {model.caveats.map((caveat) => (
          <div className="lam-caveat" key={caveat}>
            <span className="lam-caveat-mark">•</span>
            <span>{caveatText(caveat)}</span>
          </div>
        ))}
        <div className="lam-caveat">
          <span className="lam-caveat-mark">•</span>
          <span>
            Fitted from {fmtCount(model.cohort_resolved)} matched people in the seed
            {shrinkagePercent(model) < 100
              ? `, with factors pulled ${100 - shrinkagePercent(model)}% toward the average`
              : ''}
            . Computed {fmtDateTime(model.computed_at)}.
          </span>
        </div>
      </div>
    </div>
  );
}

/// Full bucket-level detail for the Planner. The builder deliberately stays compact, while the
/// Planner exposes the exact signed weights and source percentages behind each factor.
export function LookalikeDimensionDetails({ model }: { model: ModelExplanationLike }) {
  if (model.status !== 'ready') return null;
  const dimensions = model.dimensions.filter(
    (dimension) => dimension.status === 'ready' && dimension.buckets.length > 0,
  );
  if (dimensions.length === 0) return null;

  return (
    <div className="lam-dimensions">
      <div className="lam-dimensions-heading">
        <strong>Detailed factor weights</strong>
        <span>Positive values increase similarity; negative values reduce it.</span>
      </div>
      {dimensions.map((dimension) => (
        <section className="lam-dimension" key={dimension.dimension}>
          <h4>{dimensionLabel(dimension.dimension)}</h4>
          <LookalikeDimensionWeights model={model} dimension={dimension} />
        </section>
      ))}
    </div>
  );
}

/// The legend entry for the fourth comparison series the Planner gains.
///
/// A 45° hatch on `--accent`, not a new hue: `--good`/`--warn`/`--bad` are reserved status
/// tokens and would read as a verdict on the audience, and `--accent-2` is the same hue as
/// `--accent` so it is not distinguishable as a series.
export function LookalikeSeriesLegend() {
  return (
    <span>
      <span className="lam-legend-swatch lam-series-model" />
      Seed cohort
    </span>
  );
}
